import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListObjectSharingResult } from '@whinerp/client';
import { ErrorState, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import type { MessageKey } from '@whinerp/contracts/i18n';
import { useErrorMessage, useT } from '../../app/i18n.js';

/**
 * Setup > Sharing (F-008).
 *
 * A radio group per object, not a rules grid. Four modes, each with a line
 * saying what it means in people rather than in permissions - "the person who
 * owns it, and whoever they report to" is something an administrator can check
 * against their own company; "private" is not.
 *
 * The criteria builder is revealed by choosing criteria and is not visible
 * otherwise, because almost no workspace needs it and every workspace would
 * have to read past it.
 */

const MODES = ['private', 'team', 'everyone', 'criteria'] as const;

export function SharingPanel({
  teams,
  roles,
}: {
  teams: Array<{ teamId: string; name: string }>;
  roles: Array<{ roleId: string; name: string }>;
}): JSX.Element {
  const t = useT();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();

  const sharing = useQuery<ListObjectSharingResult, ApiError>({
    queryKey: ['object-sharing'],
    queryFn: () => api.listObjectSharing({}),
  });

  const save = useMutation({
    mutationFn: (input: {
      objectKey: string;
      mode: (typeof MODES)[number];
      access: 'private' | 'read' | 'read-write';
      criteria?: ListObjectSharingResult['objects'][number]['criteria'];
    }) =>
      api.setObjectSharing({
        path: { objectKey: input.objectKey },
        body: {
          mode: input.mode,
          access: input.access,
          ...(input.criteria ? { criteria: input.criteria } : {}),
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['object-sharing'] });
    },
  });

  if (sharing.isPending) return <Skeleton className="h-block-lg w-full" />;
  if (sharing.isError) {
    return (
      <ErrorState
        title={t('state.error')}
        body={sharing.error.message}
        onRetry={() => void sharing.refetch()}
      />
    );
  }

  return (
    <Panel data-region="sharing" id="sharing">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('sharing.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('sharing.body')}</p>

        <ul className="flex flex-col">
          {sharing.data.objects.map((object) => (
            <li
              key={object.objectKey}
              data-sharing-object={object.objectKey}
              className="flex flex-col gap-2 border-t border-t-rule py-3 first:border-t-0"
            >
              <div className="flex flex-wrap items-baseline gap-3">
                <span className="text-body text-ink">{object.label}</span>
                {object.mode === 'private' ? null : (
                  <label className="ms-auto flex items-center gap-2 text-dense text-ink-600">
                    {t('sharing.access')}
                    <select
                      data-sharing-access={object.objectKey}
                      value={object.access === 'private' ? 'read' : object.access}
                      onChange={(event) =>
                        save.mutate({
                          objectKey: object.objectKey,
                          mode: object.mode,
                          access: event.currentTarget.value as 'read' | 'read-write',
                          criteria: object.criteria ?? undefined,
                        })
                      }
                      className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
                    >
                      <option value="read">{t('sharing.access.read')}</option>
                      <option value="read-write">{t('sharing.access.read-write')}</option>
                    </select>
                  </label>
                )}
              </div>

              {/* Radio buttons, with the meaning under each rather than beside
                  the whole group. A person choosing is comparing sentences. */}
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {MODES.map((mode) => {
                  // An object with no owner has nobody to be private from, and
                  // the control says so rather than accepting and widening.
                  const impossible = mode === 'private' && !object.owned;
                  return (
                    <label
                      key={mode}
                      className={impossible ? 'flex gap-2 opacity-60' : 'flex gap-2'}
                      title={impossible ? t('sharing.noOwner') : undefined}
                    >
                      <input
                        type="radio"
                        name={`sharing-${object.objectKey}`}
                        data-sharing-mode={`${object.objectKey}:${mode}`}
                        checked={object.mode === mode}
                        disabled={impossible}
                        onChange={() =>
                          save.mutate({
                            objectKey: object.objectKey,
                            mode,
                            access: mode === 'private' ? 'private' : object.access === 'private' ? 'read' : object.access,
                            criteria:
                              mode === 'criteria'
                                ? (object.criteria ?? {
                                    conditions: { all: [] },
                                    access: 'read',
                                    grantTeamId: teams[0]?.teamId ?? null,
                                    grantRoleId: teams[0] ? null : (roles[0]?.roleId ?? null),
                                  })
                                : undefined,
                          })
                        }
                        className="mt-1"
                      />
                      <span className="flex flex-col">
                        <span className="text-body text-ink">{t(`sharing.mode.${mode}` as MessageKey)}</span>
                        <span className="text-dense text-ink-meta">
                          {t(`sharing.mode.${mode}.help` as MessageKey)}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>

              {/* Revealed by the choice, never sitting there. */}
              {object.mode === 'criteria' && object.criteria ? (
                <div
                  data-sharing-criteria={object.objectKey}
                  className="flex flex-wrap items-center gap-3 rounded-card bg-sunken p-3"
                >
                  <label className="flex items-center gap-2 text-dense text-ink-600">
                    {t('sharing.criteria.team')}
                    <select
                      data-criteria-team={object.objectKey}
                      value={object.criteria.grantTeamId ?? ''}
                      onChange={(event) =>
                        save.mutate({
                          objectKey: object.objectKey,
                          mode: 'criteria',
                          access: object.access === 'private' ? 'read' : object.access,
                          criteria: {
                            ...object.criteria!,
                            grantTeamId: event.currentTarget.value === '' ? null : event.currentTarget.value,
                            grantRoleId: null,
                          },
                        })
                      }
                      className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
                    >
                      {teams.map((team) => (
                        <option key={team.teamId} value={team.teamId}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Num className="text-dense text-ink-meta">
                    {String((object.criteria.conditions as { all?: unknown[] }).all?.length ?? 0)}
                  </Num>
                </div>
              ) : null}
            </li>
          ))}
        </ul>

        {save.isError ? (
          <p data-sharing-refusal className="text-dense text-danger-text">
            {errorMessage(save.error)}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}
